import { Capacitor } from '@capacitor/core'
import { BleClient } from '@capacitor-community/bluetooth-le'

let initPromise: Promise<void> | null = null
let initSucceeded = false

/**
 * Idempotent BLE stack init with Nearby-devices-only permissions on API 31+.
 * Coalesces concurrent callers; clears on failure so retry works.
 */
export async function ensureBleInitialized(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (initSucceeded && initPromise) return initPromise

  if (!initPromise) {
    initPromise = (async () => {
      await BleClient.initialize({ androidNeverForLocation: true })
      initSucceeded = true
    })().catch((err) => {
      initPromise = null
      initSucceeded = false
      throw err
    })
  }

  return initPromise
}

/** Session peek — does not prompt. */
export function isBleInitialized(): boolean {
  return initSucceeded
}

/**
 * Whether BLE / Nearby devices is granted for this session.
 * Does not prompt; call ensureBleInitialized / promptForBlePermissions to request.
 */
export async function checkBlePermissionGranted(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true
  return initSucceeded
}

/** Prompt Nearby devices (and legacy location on API ≤30) via BLE initialize. */
export async function promptForBlePermissions(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return true
  try {
    if (!initSucceeded) {
      initPromise = null
    }
    await ensureBleInitialized()
    return true
  } catch {
    return false
  }
}
